import { type z } from 'zod';

import { assertArrayIsNotEmpty, isNonEmptyArray } from '@craft/lib';

import { EnvironmentVariableErrorCode, isEnvironmentVariableErrorCode } from './types';

const groupErrorsBySource = (
  errors: (DuplicateEnvironmentVariableSourceError | EnvironmentVariableError)[],
) => {
  const grouped = new Map<
    string,
    (DuplicateEnvironmentVariableSourceError | EnvironmentVariableError)[]
  >();
  for (const error of errors) {
    const group = grouped.get(error.source) ?? [];
    group.push(error);
    grouped.set(error.source, group);
  }
  return grouped;
};

const stringifyDetail = (detail: string | z.ZodIssue) =>
  typeof detail === 'string' ? detail : `(code = ${detail.code}) ${detail.message}`;

const isZodError = (
  error?: EnvironmentVariableErrorCode | string | z.ZodError,
): error is z.ZodError => typeof error !== 'string' && error?.errors !== undefined;

const DefaultEnvironmentVariableErrorMessages: Record<
  EnvironmentVariableErrorCode,
  (source: string, punctuation?: '!' | '.' | ':') => string
> = {
  [EnvironmentVariableErrorCode.Invalid]: (source, punctuation = '!') =>
    `The environment variable '${source}' is invalid${punctuation}`,
  [EnvironmentVariableErrorCode.Missing]: (source, punctuation = '!') =>
    `The environment variable '${source}' is required but not in the environment${punctuation}`,
  [EnvironmentVariableErrorCode.Unknown]: (source, punctuation = '!') =>
    `The environment variable '${source}' is invalid or missing${punctuation}`,
};

export abstract class AbstractEnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

abstract class AbstractEnvironmentVariableError extends AbstractEnvironmentError {
  readonly source: string;

  constructor(source: string, message: string) {
    super(message);
    this.message = message;
    this.source = source;
  }

  public abstract get issues(): (string | z.ZodIssue)[];
}

const parseEnvironmentVariableErrorMessage = (
  source: string,
  error?: EnvironmentVariableErrorCode | string | z.ZodError,
  code?: EnvironmentVariableErrorCode,
): string => {
  const parsedCode = isEnvironmentVariableErrorCode(error) ? error : code;
  const parsedError = isEnvironmentVariableErrorCode(error) ? undefined : error;

  if (isZodError(parsedError)) {
    assertArrayIsNotEmpty(parsedError.errors);
    const prefix = DefaultEnvironmentVariableErrorMessages[EnvironmentVariableErrorCode.Invalid](
      source,
      ':',
    );
    if (parsedError.errors.length > 1) {
      return [
        prefix,
        ...parsedError.errors.map((detail, index) => `${index + 1}. ${stringifyDetail(detail)}`),
      ].join(' ');
    }
    return `${prefix} ${stringifyDetail(parsedError.errors[0])}`;
  }

  if (parsedError && parsedCode) {
    const prefix = DefaultEnvironmentVariableErrorMessages[parsedCode](source, ':');
    return [prefix, error].join(' ');
  }
  return (
    parsedError ??
    DefaultEnvironmentVariableErrorMessages[parsedCode ?? EnvironmentVariableErrorCode.Unknown](
      source,
    )
  );
};

/**
 * An {@link Error} that is thrown when an environment variable is invalid or missing.
 */
export class EnvironmentVariableError extends AbstractEnvironmentVariableError {
  public readonly code: EnvironmentVariableErrorCode;
  public readonly error: undefined | z.ZodError;

  constructor(source: string, error?: z.ZodError);
  constructor(source: string, code?: EnvironmentVariableErrorCode);
  constructor(source: string, message?: string, code?: EnvironmentVariableErrorCode);
  constructor(
    source: string,
    error?: EnvironmentVariableErrorCode | string | z.ZodError,
    code?: EnvironmentVariableErrorCode,
  ) {
    super(source, parseEnvironmentVariableErrorMessage(source, error, code));
    this.error = isZodError(error) ? error : undefined;
    this.code = isEnvironmentVariableErrorCode(error)
      ? error
      : isZodError(error)
        ? EnvironmentVariableErrorCode.Invalid
        : (code ?? EnvironmentVariableErrorCode.Unknown);
  }

  public static fromZodIssue(issue: z.ZodIssue): EnvironmentVariableError {
    const source = issue.path.join('.');
    return new EnvironmentVariableError(
      source,
      issue.message,
      EnvironmentVariableErrorCode.Invalid,
    );
  }

  public static Invalid(source: string, message?: string) {
    return new EnvironmentVariableError(source, message, EnvironmentVariableErrorCode.Invalid);
  }

  public static Missing(source: string, message?: string) {
    return new EnvironmentVariableError(source, message, EnvironmentVariableErrorCode.Missing);
  }

  public get issues(): (string | z.ZodIssue)[] {
    if (this.error && isZodError(this.error)) {
      return this.error.errors;
    }
    return [this.message];
  }
}

/**
 * An {@link Error} that is thrown when an attempt is made to access an environment variable on
 * the client when it is not safe to access the environment variable on the client.
 */
export class UnsafeEnvironmentVariableAccessError extends AbstractEnvironmentVariableError {
  constructor(source: string) {
    super(source, `The environment variable '${source}' is not safe to access on the client!`);
  }

  public get issues() {
    return [this.message];
  }
}

/**
 * Converts an array of {@link EnvironmentVariableError} or
 * {@link DuplicateEnvironmentVariableSourceError} errors into a string formatted error message that
 * can be displayed or logged.
 *
 * @param {(EnvironmentVariableError | DuplicateEnvironmentVariableSourceError)[]} errors
 *   An array of {@link EnvironmentVariableError} or {@link DuplicateEnvironmentVariableSourceError}
 *   errors that each correspond to an individual environment variable that failed validation.
 *
 * @returns {string}
 *   A string error message containing the information in the provided
 *   {@link EnvironmentVariableError} or {@link DuplicateEnvironmentVariableSourceError} errors.
 */
const stringifyEnvironmentErrorMessage = (
  errors: (DuplicateEnvironmentVariableSourceError | EnvironmentVariableError)[],
): string => {
  assertArrayIsNotEmpty(errors);
  return [
    'Detected invalid environment variables:',
    ...Array.from(groupErrorsBySource(errors).entries()).reduce(
      (acc: string[], [source, errs], index) => {
        assertArrayIsNotEmpty(errs);
        const prefix = `${index + 1}. ${source}: `;

        const flattenedDetails = errs.flatMap(err => err.issues);
        if (flattenedDetails.length === 1) {
          return [...acc, `\n${prefix} ${stringifyDetail(flattenedDetails[0])}`];
        }
        return [
          ...acc,
          `\n${prefix}`,
          ...flattenedDetails.map(
            (detail, subIndex) =>
              `\n${' '.repeat(prefix.length)}${subIndex + 1}. ${stringifyDetail(detail)}`,
          ),
        ];
      },
      [],
    ),
  ].join('');
};

export class DuplicateEnvironmentVariableSourceError extends AbstractEnvironmentVariableError {
  constructor(source: string) {
    super(source, `The environment variable source '${source}' is defined multiple times!`);
  }

  public get issues() {
    return [this.message];
  }
}

/**
 * An {@link Error} that is thrown when the overall environment configuration for a given package,
 * app or service in the monorepo is invalid, due to one or more environment variables being invalid
 * and/or missing.
 */
export class EnvironmentError extends AbstractEnvironmentError {
  public readonly errors: (DuplicateEnvironmentVariableSourceError | EnvironmentVariableError)[];

  constructor(
    errors: [
      DuplicateEnvironmentVariableSourceError | EnvironmentVariableError,
      ...(DuplicateEnvironmentVariableSourceError | EnvironmentVariableError)[],
    ],
  ) {
    super(stringifyEnvironmentErrorMessage(errors));
    this.errors = errors;
  }

  public static fromZodError(error: z.ZodError): EnvironmentError {
    const errors = error.issues.map(issue => EnvironmentVariableError.fromZodIssue(issue));
    if (isNonEmptyArray(errors)) {
      return new EnvironmentError(errors);
    }
    throw new Error('The ZodError contained no issues and does not represent a failed validation.');
  }
}

export class EnvironmentConfigurationError extends AbstractEnvironmentError {
  constructor(message: string) {
    super(`Invalid environment configuration: ${message}`);
  }
}
